import { Auth, Hub } from 'aws-amplify';
import awsConfig from '../aws-exports';
import { getInjectedObjectFromNativeApp, isReactNativeWebView } from '../utils/reactNativeUtils';

export type WebViewAuth = {
    userId: string;
    idToken: string;
    accessToken: string;
    refreshToken: string;
};

export const validateWebViewAuth = (auth: WebViewAuth): auth is WebViewAuth => {
    return (
        auth != null &&
        typeof auth.userId === 'string' &&
        auth.userId.length > 0 &&
        typeof auth.idToken === 'string' &&
        auth.idToken.length > 0 &&
        typeof auth.accessToken === 'string' &&
        auth.accessToken.length > 0 &&
        typeof auth.refreshToken === 'string' &&
        auth.refreshToken.length > 0
    );
};

/**
 * Nativeアプリから注入された認証トークンを取得する。
 * トークンが存在しない、または不正な場合はnullを返す。
 * @returns {WebViewAuth | null}
 */
export const getWebViewAuthTokens = (): WebViewAuth | null => {
    const isNativeWebView = isReactNativeWebView();
    if (!isNativeWebView) return null;

    const nativeObject = getInjectedObjectFromNativeApp() as { authorization: WebViewAuth } | null;

    if (nativeObject == null || nativeObject.authorization == null) {
        console.error('No authorization object found in injected data.');
        return null;
    }

    const authorization = nativeObject.authorization;
    if (!validateWebViewAuth(authorization)) {
        console.error('Invalid authorization object found in injected data.');
        return null;
    }
    return authorization;
};

// localStorageにトークンを保存することでサインインを行う。
export const signInWithTokens = async (tokens: WebViewAuth) => {
    try {
        const createKey = (tokenType: string) =>
            `CognitoIdentityServiceProvider.${awsConfig.aws_user_pools_web_client_id}.${tokens.userId}.${tokenType}`;

        const LastAuthUserKey = `CognitoIdentityServiceProvider.${awsConfig.aws_user_pools_web_client_id}.LastAuthUser`;

        localStorage.setItem(LastAuthUserKey, tokens.userId);
        localStorage.setItem(createKey('idToken'), tokens.idToken);
        localStorage.setItem(createKey('accessToken'), tokens.accessToken);
        localStorage.setItem(createKey('refreshToken'), tokens.refreshToken);
        localStorage.setItem(createKey('clockDrift'), '0');

        Hub.dispatch('auth', { event: 'signIn' }, 'Auth');
        const session = await Auth.currentSession();

        if (session.isValid()) {
            // useFujiAuthと同様にユーザー情報を適用する。
            // bypassCacheをtrueにすることで、キャッシュではなく上で適用した情報が適用される。
            await Auth.currentAuthenticatedUser({ bypassCache: true });
        }
    } catch (error) {
        console.error('Error during signinWithTokens:', error);
        throw error;
    }
};

/**
 * 入力された認証情報と現在の認証ユーザーが同一かどうかを確認する。
 */
export const checkIsSameUser = async (authorization: WebViewAuth): Promise<boolean> => {
    const currentUser = await Auth.currentAuthenticatedUser().catch(() => null);
    if (!currentUser) {
        return false;
    }
    return currentUser.username === authorization.userId;
};
